import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, ArrowRight, ArrowLeft, Check, Lock, Globe, User as UserIcon, Users,
  Calendar as CalendarIcon, Clock, Loader2, Search, Mail, Eye,
} from 'lucide-react';
import { toLocalInputValue, localInputToUtcIso, parseUtc, guessUserTz } from '../lib/calendarDate';
import SessionPreviewModal from './SessionPreviewModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Brief Jun 16 §2 — Visibility × Format combinations → backend event_type
// Per user choice 1c, "private + groupe" reuses private_1on1 with capacity > 1.
const TYPE_MAP = {
  'private-solo':  'private_1on1',
  'private-group': 'private_1on1',  // capacity > 1
  'public-solo':   'public_slot',
  'public-group':  'public_event',
};

const DURATION_OPTIONS = [
  { value: 30,  label: '30 min' },
  { value: 60,  label: '1 h' },
  { value: 90,  label: '1 h 30' },
  { value: 120, label: '2 h' },
  // Brief audit beta Feb 2026 (P1 §1) — option "Personnalisée" qui affiche
  // un time picker "Heure de fin" au lieu d'une durée fixe.
  { value: 'custom', label: 'Personnalisée…' },
];

const todayPlusOne = () => {
  const now = new Date();
  now.setHours(now.getHours() + 1, 0, 0, 0);
  return now;
};

const buildInitialForm = () => {
  const start = todayPlusOne();
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    visibility: 'public',  // 'private' | 'public'
    format: 'solo',        // 'solo' | 'group'
    title: '',
    description: '',
    start_local: toLocalInputValue(start),
    duration_minutes: 60,
    // Brief audit beta Feb 2026 (P1 §1) — mode 'custom' active un time picker
    // fin. end_local reflète l'heure de fin choisie ; sinon on la calcule.
    duration_mode: 'preset', // 'preset' | 'custom'
    end_local: toLocalInputValue(end),
    max_capacity: 10,
    invited_user_id: '',
    invited_user_display_name: '',
    invitation_email: '',
    // Brief audit beta Feb 2026 (P1 §4) — lien de la réunion (Jitsi auto-généré
    // ou saisi manuellement par le user).
    visio_link: '',
    // Brief Jul 6 2026 §1 — Prix (masqué + forcé à 0 quand FORCE_FREE_SESSIONS=true).
    price: 0,
  };
};

const InviteeSearch = ({ form, updateForm }) => {
  const [q, setQ] = useState(form.invited_user_display_name || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q || q.length < 1) { setResults([]); return; }
    const t = setTimeout(() => {
      setLoading(true);
      axios.get(`${API}/social/search-users?q=${encodeURIComponent(q)}`, { withCredentials: true })
        .then(r => setResults(r.data?.users || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-300 mb-1">Inviter (utilisateur OpenSapiens)</label>
      <div className="relative">
        <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          data-testid="session-wizard-invitee-search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            if (form.invited_user_id) updateForm({ invited_user_id: '', invited_user_display_name: '' });
          }}
          placeholder="Tape un nom..."
          className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40"
        />
        {open && q.length >= 1 && (
          <div className="absolute z-30 mt-1 w-full max-h-48 overflow-y-auto rounded-lg bg-[#0d1f35] border border-white/10 shadow-xl">
            {loading && <div className="p-3 text-xs text-gray-400 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Recherche…</div>}
            {!loading && results.length === 0 && <div className="p-3 text-xs text-gray-400">Aucun utilisateur. Essaie un email ci-dessous.</div>}
            {!loading && results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                data-testid={`session-wizard-invitee-result-${u.user_id}`}
                onClick={() => {
                  updateForm({ invited_user_id: u.user_id, invited_user_display_name: u.display_name, invitation_email: '' });
                  setQ(u.display_name);
                  setOpen(false);
                }}
                className="w-full flex items-center gap-2 p-2 hover:bg-[#7F77DD]/10 text-left"
              >
                <div className="h-6 w-6 rounded-full bg-[#7F77DD]/20 flex items-center justify-center text-[#bcb6f3] text-[10px] font-medium">
                  {u.display_name?.[0]?.toUpperCase() || '?'}
                </div>
                <span className="text-sm text-white">{u.display_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {form.invited_user_id && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-[#7F77DD]/15 border border-[#7F77DD]/40" data-testid="session-wizard-invitee-selected">
          <Check className="h-4 w-4 text-[#bcb6f3]" />
          <span className="text-xs text-[#dcd9fb]">Invité : <strong>{form.invited_user_display_name}</strong></span>
        </div>
      )}
      <div className="text-center text-xs text-gray-500">— ou —</div>
      <div>
        <label className="block text-xs text-gray-300 mb-1 flex items-center gap-1"><Mail className="h-3 w-3" /> Email (hors OpenSapiens)</label>
        <input
          data-testid="session-wizard-invitee-email"
          type="email"
          value={form.invitation_email}
          onChange={(e) => updateForm({ invitation_email: e.target.value, invited_user_id: '', invited_user_display_name: '' })}
          placeholder="prenom.nom@email.com"
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500"
        />
      </div>
    </div>
  );
};

const ChoiceCard = ({ active, onClick, icon: Icon, label, hint, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`flex-1 text-left p-3 rounded-xl border transition-all ${
      active
        ? 'bg-[#7F77DD]/15 border-[#7F77DD]/60 ring-1 ring-[#7F77DD]/40'
        : 'bg-white/5 border-white/10 hover:bg-white/8'
    }`}
  >
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`h-4 w-4 ${active ? 'text-[#bcb6f3]' : 'text-gray-400'}`} />
      <span className={`font-semibold text-sm ${active ? 'text-white' : 'text-gray-200'}`}>{label}</span>
    </div>
    <p className="text-xs text-gray-400">{hint}</p>
  </button>
);

const SessionWizard = ({ isOpen, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(buildInitialForm());
  const [submitting, setSubmitting] = useState(false);
  const [createdEvent, setCreatedEvent] = useState(null);

  // Stripe LIVE (Sep 2026) — CTA « Connecter Stripe » (onboarding Express hosted)
  const connectStripe = async () => {
    try {
      const { data } = await axios.post(`${API}/stripe/connect/onboard`, {}, { withCredentials: true });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Onboarding Stripe indisponible.');
    }
  };
  // Brief pré-beta (Task 1) — brouillon : pas de notification followers.
  const [savedDraft, setSavedDraft] = useState(false);
  // Brief Jul 6 2026 P1 — aperçu participant depuis le wizard (étape 2).
  const [previewOpen, setPreviewOpen] = useState(false);
  // Brief Jul 6 2026 §1 — Fetch DB-backed FORCE_FREE_SESSIONS AT MOUNT (jamais
  // en cache statique build-time). Si false → champ prix visible + obligatoire.
  const [forceFree, setForceFree] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setForm(buildInitialForm());
      setCreatedEvent(null);
      setSavedDraft(false);
      // Fetch au chaque ouverture pour toujours refléter le state actuel du toggle admin.
      axios
        .get(`${API}/admin/settings/force-free-sessions`, { withCredentials: true })
        .then(({ data }) => setForceFree(!!data?.force_free_sessions))
        .catch(() => setForceFree(true)); // fallback safe : masquer prix si erreur
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const eventType = TYPE_MAP[`${form.visibility}-${form.format}`];
  const needsInvitee = form.visibility === 'private' && form.format === 'solo';
  const isGroup = form.format === 'group';

  const submit = async (asDraft = false) => {
    if (!form.title.trim()) { toast.error('Donne un titre à ta session.'); return; }
    if (!form.start_local) { toast.error('Choisis une date et une heure.'); return; }
    if (needsInvitee && !form.invited_user_id && !form.invitation_email) {
      toast.error('Pour une session privée 1-to-1, indique un invité ou un email.');
      return;
    }
    const startDate = new Date(form.start_local);
    // Brief audit beta Feb 2026 (P1 §1) — durée personnalisée : on part de end_local
    // et on calcule les minutes. Minimum 15 min.
    let effectiveDuration = form.duration_minutes;
    let endDate;
    if (form.duration_mode === 'custom') {
      if (!form.end_local) { toast.error('Choisis une heure de fin.'); return; }
      endDate = new Date(form.end_local);
      const diffMin = Math.round((endDate.getTime() - startDate.getTime()) / 60000);
      if (diffMin < 15) { toast.error('La session doit durer au moins 15 minutes.'); return; }
      effectiveDuration = diffMin;
    } else {
      endDate = new Date(startDate.getTime() + effectiveDuration * 60 * 1000);
    }

    setSubmitting(true);
    try {
      // Brief Jul 6 2026 §1 — Prix envoyé UNIQUEMENT si le mode paid est actif
      // (force_free=false). Sinon on force 0 + is_free=true.
      const paidMode = forceFree === false;
      const priceNum = paidMode ? Math.max(0, Number(form.price) || 0) : 0;
      const payload = {
        type: eventType,
        title: form.title.trim(),
        description: form.description.trim(),
        start_time: localInputToUtcIso(form.start_local),
        end_time: localInputToUtcIso(toLocalInputValue(endDate)),
        duration_minutes: effectiveDuration,
        max_capacity: isGroup ? Math.max(2, form.max_capacity) : 1,
        is_free: !paidMode || priceNum === 0,
        price: priceNum,
        currency: 'CHF',
        // Brief pré-beta (Task 1) — brouillon : invisible + aucune notification.
        status: asDraft ? 'draft' : 'published',
      };
      // Brief audit beta Feb 2026 (P1 §4) — visio_link optionnel envoyé au backend.
      if (form.visio_link && form.visio_link.trim()) {
        payload.visio_link = form.visio_link.trim();
      }
      if (eventType === 'private_1on1') {
        if (form.invited_user_id) payload.invited_user_id = form.invited_user_id;
        if (form.invitation_email) payload.invitation_email = form.invitation_email;
      }
      const { data } = await axios.post(`${API}/calendar/events`, payload, { withCredentials: true });
      setCreatedEvent(data);
      setSavedDraft(asDraft);
      setStep(3);
      onCreated?.(data);
      toast.success(asDraft ? 'Brouillon enregistré' : 'Session créée !');
      // Stripe LIVE (Sep 2026) — session payante publiée sans Stripe connecté :
      // publication OK, mais on invite l'expert à finaliser l'onboarding.
      if (data?.stripe_setup_required) {
        toast.warning('Ta session est publiée. Connecte Stripe pour recevoir les paiements.', {
          duration: 12000,
          action: { label: 'Connecter Stripe', onClick: connectStripe },
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Création impossible.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-5" data-testid="session-wizard-step-1">
      <div>
        <h4 className="text-sm font-semibold text-white mb-2">Visibilité</h4>
        <div className="flex gap-2">
          <ChoiceCard
            active={form.visibility === 'private'}
            onClick={() => updateForm({ visibility: 'private' })}
            icon={Lock}
            label="Privé"
            hint="Sur invitation uniquement"
            testid="session-wizard-visibility-private"
          />
          <ChoiceCard
            active={form.visibility === 'public'}
            onClick={() => updateForm({ visibility: 'public' })}
            icon={Globe}
            label="Public"
            hint="Visible sur ton profil et réservable par tous"
            testid="session-wizard-visibility-public"
          />
        </div>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-white mb-2">Format</h4>
        <div className="flex gap-2">
          <ChoiceCard
            active={form.format === 'solo'}
            onClick={() => updateForm({ format: 'solo' })}
            icon={UserIcon}
            label="Seul (1-to-1)"
            hint="Une personne à la fois"
            testid="session-wizard-format-solo"
          />
          <ChoiceCard
            active={form.format === 'group'}
            onClick={() => updateForm({ format: 'group' })}
            icon={Users}
            label="Groupe"
            hint="Plusieurs participants"
            testid="session-wizard-format-group"
          />
        </div>
      </div>
      {/* Brief Jul 6 2026 §2 — Label technique "Type sélectionné : public_slot"
          RETIRÉ. L'utilisateur ne doit voir que ses choix (Privé/Public +
          Seul/Groupe), pas le type interne en base. On garde uniquement le
          rappel de capacité pour les groupes. */}
      {isGroup && (
        <div className="text-xs text-gray-400 px-3 py-2 rounded-lg bg-white/5 border border-white/10" data-testid="session-wizard-capacity-hint">
          Capacité par défaut : <strong className="text-[#bcb6f3]">{form.max_capacity}</strong>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4" data-testid="session-wizard-step-2">
      <div>
        <label className="block text-xs font-medium text-gray-300 mb-1">Titre *</label>
        <input
          data-testid="session-wizard-title"
          value={form.title}
          onChange={(e) => updateForm({ title: e.target.value })}
          placeholder="Ex : Coaching gestion de fortune"
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-300 mb-1">Description (optionnel)</label>
        <textarea
          data-testid="session-wizard-description"
          value={form.description}
          onChange={(e) => updateForm({ description: e.target.value })}
          rows={2}
          placeholder="Ce que les participants apprendront…"
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40 text-sm resize-none"
        />
      </div>
      {/* Brief Jul 6 2026 §1 — Champ prix visible UNIQUEMENT quand
          FORCE_FREE_SESSIONS=false (Stripe Connect actif). Sinon session
          gratuite forcée côté backend. */}
      {forceFree === false && (
        <div data-testid="session-wizard-price-block">
          <label className="block text-xs font-medium text-gray-300 mb-1">Prix (CHF) *</label>
          <div className="relative">
            <input
              data-testid="session-wizard-price"
              type="number"
              min="0"
              step="1"
              value={form.price}
              onChange={(e) => updateForm({ price: e.target.value })}
              placeholder="0 pour session gratuite"
              className="w-full pl-3 pr-14 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40 text-sm"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">CHF</span>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">
            Le paiement va directement sur ton compte Stripe (0 % commission beta). Saisis 0 pour une session gratuite.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Date et heure de début *</label>
          <input
            data-testid="session-wizard-start"
            type="datetime-local"
            value={form.start_local}
            onChange={(e) => updateForm({ start_local: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40"
          />
          {/* Fix timezone Jul 2026 — badge de réassurance heure locale */}
          <p className="flex items-center gap-1.5 text-[11px] text-cyan-300/80 mt-1.5" data-testid="wizard-timezone-badge">
            <Globe className="h-3 w-3 flex-shrink-0" />
            Heure locale ({guessUserTz()}) — vos participants la verront dans leur propre fuseau horaire
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Durée *</label>
          <select
            data-testid="session-wizard-duration"
            value={form.duration_mode === 'custom' ? 'custom' : form.duration_minutes}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'custom') {
                // Initialize end_local to start + 1h if not set yet
                const startDate = form.start_local ? new Date(form.start_local) : todayPlusOne();
                const currentEnd = form.end_local ? new Date(form.end_local) : null;
                const nextEnd = (currentEnd && currentEnd > startDate)
                  ? currentEnd
                  : new Date(startDate.getTime() + (form.duration_minutes || 60) * 60000);
                updateForm({ duration_mode: 'custom', end_local: toLocalInputValue(nextEnd) });
              } else {
                updateForm({ duration_mode: 'preset', duration_minutes: parseInt(v, 10) });
              }
            }}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40"
          >
            {DURATION_OPTIONS.map(o => <option key={o.value} value={o.value} className="bg-[#0d1f35]">{o.label}</option>)}
          </select>
        </div>
      </div>
      {/* Brief audit beta Feb 2026 (P1 §1) — time picker heure de fin en mode custom */}
      {form.duration_mode === 'custom' && (
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Heure de fin *</label>
          <input
            data-testid="session-wizard-end"
            type="datetime-local"
            value={form.end_local}
            min={form.start_local}
            onChange={(e) => updateForm({ end_local: e.target.value })}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40"
          />
          <p className="text-[10px] text-gray-500 mt-1">Minimum 15 minutes après le début.</p>
        </div>
      )}
      {/* Brief audit beta Feb 2026 (P1 §4) — champ lien de réunion + générateur Jitsi */}
      <div>
        <label className="block text-xs font-medium text-gray-300 mb-1">Lien de la réunion (optionnel)</label>
        <div className="flex gap-2">
          <input
            data-testid="session-wizard-visio-link"
            type="url"
            value={form.visio_link}
            onChange={(e) => updateForm({ visio_link: e.target.value })}
            placeholder="https://meet.jit.si/… ou Zoom, Teams, Meet…"
            className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7F77DD]/40"
          />
          <button
            type="button"
            onClick={() => {
              const slug = `opensapiens-${Math.random().toString(36).slice(2, 10)}`;
              updateForm({ visio_link: `https://meet.jit.si/${slug}` });
              toast.success('Lien Jitsi généré');
            }}
            className="px-3 py-2 rounded-lg bg-[#7F77DD]/20 border border-[#7F77DD]/40 text-[#bcb6f3] text-xs font-medium hover:bg-[#7F77DD]/30"
            title="Génère un lien de réunion vidéo gratuit"
            data-testid="session-wizard-generate-jitsi"
          >
            Générer lien
          </button>
        </div>
      </div>
      {isGroup && (
        <div>
          <label className="block text-xs font-medium text-gray-300 mb-1">Nombre max de participants</label>
          <input
            data-testid="session-wizard-capacity"
            type="number"
            min="2"
            max="200"
            value={form.max_capacity}
            onChange={(e) => updateForm({ max_capacity: Math.max(2, parseInt(e.target.value, 10) || 2) })}
            className="w-32 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
          />
        </div>
      )}
      {needsInvitee && <InviteeSearch form={form} updateForm={updateForm} />}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5 text-center py-6" data-testid="session-wizard-step-3">
      <div className="mx-auto w-16 h-16 rounded-full bg-[#7F77DD]/15 border border-[#7F77DD]/40 flex items-center justify-center">
        <Check className="h-8 w-8 text-[#bcb6f3]" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-white">{savedDraft ? 'Brouillon enregistré' : 'Session publiée'}</h3>
        <p className="text-sm text-gray-400 mt-1">
          {savedDraft
            ? 'Ta session est enregistrée mais pas encore visible. Publie-la depuis « Mes sessions » — tes followers ne sont pas notifiés tant qu\u2019elle reste en brouillon.'
            : 'Elle est désormais visible dans ton calendrier.'}
        </p>
      </div>
      {createdEvent && (
        <div className="mx-auto max-w-md p-4 rounded-xl bg-white/5 border border-white/10 text-left space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <CalendarIcon className="h-4 w-4 text-[#bcb6f3]" />
            <span className="text-white font-medium">{createdEvent.title}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-300">
            <Clock className="h-3.5 w-3.5 text-gray-400" />
            {parseUtc(createdEvent.start_time)?.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
          <div className="text-xs text-gray-400">Type : <strong className="text-[#bcb6f3]">{createdEvent.type}</strong></div>
          {/* Stripe LIVE (Sep 2026) — session payante publiée sans Stripe connecté */}
          {createdEvent.stripe_setup_required && (
            <div
              className="mt-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30"
              data-testid="wizard-stripe-setup-banner"
            >
              <p className="text-xs text-amber-300 font-semibold">
                Ta session est publiée. Connecte Stripe pour recevoir les paiements.
              </p>
              <p className="text-[11px] text-amber-200/70 mt-1">
                En attendant, les visiteurs voient « Paiement en cours de configuration ».
                100% du montant te revient — aucune commission OpenSapiens.
              </p>
              <button
                type="button"
                onClick={connectStripe}
                data-testid="wizard-connect-stripe-btn"
                className="mt-2 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold transition-colors"
              >
                Connecter Stripe
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="session-wizard-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl bg-gradient-to-br from-[#0d1f35] via-[#0f2540] to-[#0d1f35] border border-white/10 shadow-2xl p-6 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-[#bcb6f3]" />
            <h2 className="text-lg font-semibold text-white">Créer une session</h2>
          </div>
          <button data-testid="session-wizard-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex items-center gap-2 mb-6 flex-shrink-0">
          {[1, 2, 3].map((i) => (
            <React.Fragment key={i}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                step === i ? 'bg-[#7F77DD]/20 border border-[#7F77DD]/60 text-[#bcb6f3]'
                : step > i ? 'bg-[#7F77DD]/40 text-white'
                : 'bg-white/5 text-gray-500 border border-white/10'
              }`}>
                {step > i ? <Check className="h-3.5 w-3.5" /> : i}
              </div>
              {i < 3 && <div className="flex-1 h-px bg-white/10" />}
            </React.Fragment>
          ))}
        </div>
        <div className="min-h-[300px] flex-1 overflow-y-auto pr-1">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
          <div>
            {step === 2 && (
              <button data-testid="session-wizard-back" type="button" onClick={() => setStep(1)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white">
                <ArrowLeft className="h-4 w-4" /> Retour
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {step !== 3 && (
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border border-white/10 text-gray-300 hover:bg-white/5">
                Annuler
              </button>
            )}
            {step === 1 && (
              <button data-testid="session-wizard-next" type="button" onClick={() => setStep(2)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-[#7F77DD] text-white font-medium hover:opacity-90">
                Suivant <ArrowRight className="h-4 w-4" />
              </button>
            )}
            {step === 2 && (
              <>
                <button
                  data-testid="session-wizard-save-draft"
                  type="button"
                  onClick={() => submit(true)}
                  disabled={submitting}
                  title="Enregistrer sans publier — tes followers ne seront pas notifiés"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-white/15 text-gray-300 font-medium hover:bg-white/5 disabled:opacity-50"
                >
                  Enregistrer comme brouillon
                </button>
                <button
                  data-testid="session-wizard-preview"
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm border border-[#7F77DD]/40 text-[#bcb6f3] font-medium hover:bg-[#7F77DD]/10"
                >
                  <Eye className="h-4 w-4" /> Prévisualiser
                </button>
                <button data-testid="session-wizard-submit" type="button" onClick={() => submit(false)} disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-[#7F77DD] text-white font-medium hover:opacity-90 disabled:opacity-50">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {submitting ? 'Publication…' : 'Publier la session'}
                </button>
              </>
            )}
            {step === 3 && (
              <button data-testid="session-wizard-done" type="button" onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm bg-[#7F77DD] text-white font-medium">
                Terminer
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Brief Jul 6 2026 P1 — aperçu participant (non interactif) */}
      <SessionPreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        form={form}
        isGroup={isGroup}
        forceFree={forceFree}
      />
    </div>
  );
};

export default SessionWizard;
